/**
 * Owner: Masa Bayu
 * File: guards.ts
 * Date:Time: 2026-09-18 15:50
 * Description: Primitive guards for Document IR — forbid publication leakage.
 */

export const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
export const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export const QX_DOCUMENT_IR_FORBIDDEN_KEYS = [
  'qxp',
  'qxpPayload',
  'mutateQxp',
  'publish',
  'marketplace',
  'qxwContent',
  'reader',
  'qxreader',
  'react',
  'css',
  'DOM',
  'dom',
  'bytes',
  'buffer',
  'blob',
  'parser',
  'execute',
  'runtime',
] as const;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function assertValidId(id: unknown, label: string): asserts id is string {
  if (typeof id !== 'string' || id.length === 0 || /\s/.test(id)) {
    throw new Error(`${label} must be a non-empty id without whitespace`);
  }
}

export function assertTimestamp(value: unknown, label: string): asserts value is string {
  if (typeof value !== 'string' || !TIMESTAMP_PATTERN.test(value)) {
    throw new Error(`${label} must be an ISO-8601 timestamp`);
  }
}

export function assertSemver(value: unknown, label: string): asserts value is string {
  if (typeof value !== 'string' || !VERSION_PATTERN.test(value)) {
    throw new Error(`${label} must be a semver string`);
  }
}

export function assertNoForbiddenKeys(record: Record<string, unknown>, label: string): void {
  for (const key of Object.keys(record)) {
    if ((QX_DOCUMENT_IR_FORBIDDEN_KEYS as readonly string[]).includes(key)) {
      throw new Error(`${label} must not include forbidden field "${key}"`);
    }
  }
}

export function assertConfidence(value: unknown, label: string): asserts value is number {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0 || value > 1) {
    throw new Error(`${label} must be a number between 0 and 1`);
  }
}

export function assertOptionalTrimmed(value: unknown, label: string): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value.trim();
}
